import { OfficeRoom, RoomType, RoomInteractionPolicy } from '../types';
import { RoomTemplate } from '../data/officeCatalog';
import { findOverlappingRoom, findFreeRoomPosition } from './isoMath';

export interface CreateRoomParams {
  col?: number;
  row?: number;
  width?: number;
  height?: number;
  template?: RoomTemplate | Partial<OfficeRoom>;
  name?: string;
  type?: RoomType;
  color?: string;
  icon?: string;
  policy?: RoomInteractionPolicy;
  description?: string;
  gridWidth: number;
  gridHeight: number;
  existingRooms?: OfficeRoom[];
  autoRelocateOnOverlap?: boolean;
}

export interface CreateRoomResult {
  room: OfficeRoom;
  overlapConflict?: OfficeRoom;
  success: boolean;
}

/**
 * Canonical Room Creator: Shared unified logic for building rooms/zones
 * across RoomManager, TileContextMenu, Canvas, and App layout updates.
 */
export function createNewOfficeRoom(params: CreateRoomParams): CreateRoomResult {
  const {
    col,
    row,
    width,
    height,
    template,
    name,
    type = 'work',
    color = '#3b82f6',
    icon = '💻',
    policy,
    description,
    gridWidth,
    gridHeight,
    existingRooms = [],
    autoRelocateOnOverlap = true,
  } = params;

  const id = `room_${Date.now()}_${Math.floor(Math.random() * 1000)}`;

  let createdRoom: OfficeRoom;

  if (template) {
    const tmplW = 'defaultWidth' in template ? template.defaultWidth : template.width ?? 4;
    const tmplH = 'defaultHeight' in template ? template.defaultHeight : template.height ?? 4;
    const tmplPolicy = 'defaultPolicy' in template ? template.defaultPolicy : template.policy;

    const targetW = width ?? tmplW;
    const targetH = height ?? tmplH;
    const initialCol = col !== undefined ? col : ('col' in template ? template.col ?? 1 : 1);
    const initialRow = row !== undefined ? row : ('row' in template ? template.row ?? 1 : 1);

    createdRoom = {
      id,
      name: template.name || name || 'Zone',
      type: template.type || type,
      color: template.color || color,
      icon: template.icon || icon,
      col: Math.min(initialCol, Math.max(0, gridWidth - targetW)),
      row: Math.min(initialRow, Math.max(0, gridHeight - targetH)),
      width: Math.min(targetW, gridWidth),
      height: Math.min(targetH, gridHeight),
      policy: tmplPolicy || policy || {
        energyDelta: 0.1,
        focusBonus: 10,
        bladderDelta: 0.5,
        maxCapacity: 4,
        ambientStatusText: 'Inside the zone',
      },
      description: template.description || description,
    };
  } else {
    const targetW = width ?? 4;
    const targetH = height ?? 4;
    const initialCol = col !== undefined ? col : 1;
    const initialRow = row !== undefined ? row : 1;

    createdRoom = {
      id,
      name: (name || '').trim() || 'New Work Zone',
      type,
      color,
      icon,
      col: Math.min(initialCol, Math.max(0, gridWidth - targetW)),
      row: Math.min(initialRow, Math.max(0, gridHeight - targetH)),
      width: Math.min(targetW, gridWidth),
      height: Math.min(targetH, gridHeight),
      policy: policy || {
        energyDelta: 0.2,
        focusBonus: 10,
        bladderDelta: 0.5,
        maxCapacity: 4,
        ambientStatusText: 'Focused at workstation',
      },
      description,
    };
  }

  // Check collision / overlap with existing rooms
  const overlapConflict = findOverlappingRoom(createdRoom, existingRooms);
  if (overlapConflict) {
    if (autoRelocateOnOverlap) {
      const freePos = findFreeRoomPosition(
        createdRoom.width,
        createdRoom.height,
        gridWidth,
        gridHeight,
        existingRooms
      );

      if (freePos) {
        createdRoom.col = freePos.col;
        createdRoom.row = freePos.row;
        return { room: createdRoom, success: true };
      }
    }
    return { room: createdRoom, overlapConflict, success: false };
  }

  return { room: createdRoom, success: true };
}
